<template>
    <div class="holder-analytics">
        <!-- Header -->
        <div class="analytics-header">
            <h5 class="analytics-title">
                <span class="title-icon">&#x1F465;</span>
                Holder Distribution
            </h5>
            <span class="total-holders">
                {{ formatNumber(summary.total_holders) }} holders
            </span>
        </div>

        <!-- Loading state -->
        <div v-if="loading" class="text-center py-3">
            <div class="spinner-border spinner-border-sm text-primary" role="status">
                <span class="visually-hidden">Loading...</span>
            </div>
        </div>

        <template v-if="!loading">
            <!-- Concentration Cards -->
            <div class="concentration-grid">
                <div class="concentration-card">
                    <div class="conc-label">Top 10 Holders</div>
                    <div class="conc-value">{{ formatPercent(summary.top_10_percent) }}%</div>
                    <div class="conc-bar">
                        <div class="conc-bar-fill" :style="{ width: Math.min(summary.top_10_percent || 0, 100) + '%' }"></div>
                    </div>
                </div>
                <div class="concentration-card">
                    <div class="conc-label">Top 50 Holders</div>
                    <div class="conc-value">{{ formatPercent(summary.top_50_percent) }}%</div>
                    <div class="conc-bar">
                        <div class="conc-bar-fill" :style="{ width: Math.min(summary.top_50_percent || 0, 100) + '%' }"></div>
                    </div>
                </div>
            </div>

            <!-- Top Holders Table -->
            <div class="holders-table-section">
                <div class="table-header">
                    <span class="table-title">Top Holders</span>
                    <div class="table-controls">
                        <label class="filter-toggle" title="Hide program-derived addresses (PDAs) like DEX vaults and protocol accounts">
                            <input type="checkbox" v-model="hidePrograms" />
                            <span class="filter-toggle-label">Human only</span>
                        </label>
                        <div class="per-page-selector">
                            <select v-model="perPage" @change="loadPage(1)" class="form-select form-select-sm">
                                <option :value="25">25</option>
                                <option :value="50">50</option>
                                <option :value="100">100</option>
                            </select>
                        </div>
                    </div>
                </div>

                <div class="table-responsive">
                    <table class="table table-sm holders-table mb-0">
                        <thead>
                            <tr>
                                <th class="col-rank">#</th>
                                <th>Address</th>
                                <th class="text-end">Balance</th>
                                <th class="text-end">% Supply</th>
                                <th class="text-end">USD Value</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr v-for="holder in filteredHolders" :key="holder.owner">
                                <td class="col-rank">{{ holder.rank }}</td>
                                <td>
                                    <div class="holder-info">
                                        <a :href="`https://solscan.io/account/${holder.owner}`"
                                           target="_blank"
                                           class="holder-address"
                                           :title="holder.owner">
                                            {{ holder.name || truncateAddress(holder.owner) }}
                                        </a>
                                        <span v-if="holder.name" class="holder-pubkey">
                                            {{ truncateAddress(holder.owner) }}
                                        </span>
                                        <div class="holder-labels">
                                            <span v-if="holder.is_program" class="label-badge label-program">Program</span>
                                            <span
                                                v-for="label in (holder.labels || []).slice(0, 2)"
                                                :key="label"
                                                class="label-badge"
                                                :class="`label-${label.toLowerCase().replace(/[^a-z0-9]/g, '-')}`"
                                            >
                                                {{ label }}
                                            </span>
                                        </div>
                                    </div>
                                </td>
                                <td class="text-end mono">{{ formatBalance(holder.balance) }}</td>
                                <td class="text-end mono">{{ formatPercent(holder.percent_supply) }}%</td>
                                <td class="text-end mono">${{ formatUsd(holder.balance_usd) }}</td>
                            </tr>
                            <tr v-if="holders.length === 0">
                                <td colspan="5" class="text-center text-muted py-3">No holders found</td>
                            </tr>
                        </tbody>
                    </table>
                </div>

                <!-- Pagination -->
                <div class="pagination-controls" v-if="pagination.total_pages > 1">
                    <button
                        class="page-btn"
                        :disabled="pagination.current_page <= 1"
                        @click="loadPage(pagination.current_page - 1)"
                    >
                        &laquo; Prev
                    </button>
                    <span class="page-info">
                        Page {{ pagination.current_page }} of {{ pagination.total_pages }}
                    </span>
                    <button
                        class="page-btn"
                        :disabled="!pagination.has_more"
                        @click="loadPage(pagination.current_page + 1)"
                    >
                        Next &raquo;
                    </button>
                </div>
            </div>
        </template>
    </div>
</template>

<script>
export default {
    name: 'HolderAnalytics',

    props: {
        mint: {
            type: String,
            required: true
        }
    },

    data() {
        return {
            loading: true,
            perPage: 50,
            hidePrograms: false,
            holders: [],
            summary: {
                total_holders: 0,
                total_supply: 0,
                top_10_percent: 0,
                top_50_percent: 0,
                top_100_percent: 0
            },
            pagination: {
                current_page: 1,
                per_page: 50,
                total: 0,
                total_pages: 1,
                has_more: false
            }
        };
    },

    computed: {
        filteredHolders() {
            if (!this.hidePrograms) return this.holders;
            return this.holders.filter(h => !h.is_program);
        }
    },

    mounted() {
        this.loadPage(1);
    },

    watch: {
        mint() {
            this.loadPage(1);
        }
    },

    methods: {
        async loadPage(page) {
            this.loading = true;

            try {
                const res = await fetch(`/api/tokens/${this.mint}/holders?page=${page}&per_page=${this.perPage}`);
                if (!res.ok) throw new Error('Failed to load holder data');

                const data = await res.json();

                this.holders = data.holders || [];
                this.pagination = data.pagination || this.pagination;

                // Only update summary on page 1 (where concentration metrics are calculated)
                if (page === 1 && data.summary) {
                    this.summary = data.summary;
                }

            } catch (e) {
                console.error('Error loading holder data:', e);
                this.holders = [];
            } finally {
                this.loading = false;
            }
        },

        truncateAddress(address) {
            if (!address) return 'N/A';
            return `${address.slice(0, 4)}...${address.slice(-4)}`;
        },

        formatNumber(num) {
            if (num === null || num === undefined) return '0';
            if (num >= 1e6) return (num / 1e6).toFixed(1) + 'M';
            if (num >= 1e3) return (num / 1e3).toFixed(1) + 'K';
            return num.toLocaleString();
        },

        formatBalance(balance) {
            if (balance === null || balance === undefined) return '0';
            if (balance >= 1e9) return (balance / 1e9).toFixed(2) + 'B';
            if (balance >= 1e6) return (balance / 1e6).toFixed(2) + 'M';
            if (balance >= 1e3) return (balance / 1e3).toFixed(2) + 'K';
            if (balance >= 1) return balance.toFixed(2);
            return balance.toPrecision(4);
        },

        formatPercent(pct) {
            if (pct === null || pct === undefined) return '0.00';
            if (pct >= 100) return '>99.99';
            return pct.toFixed(2);
        },

        formatUsd(value) {
            if (value === null || value === undefined || value === 0) return '0';
            if (value >= 1e9) return (value / 1e9).toFixed(2) + 'B';
            if (value >= 1e6) return (value / 1e6).toFixed(2) + 'M';
            if (value >= 1e3) return (value / 1e3).toFixed(2) + 'K';
            return value.toFixed(2);
        }
    }
};
</script>

<style scoped>
.holder-analytics {
    background: var(--compass-bg-secondary, #1a1a2e);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    border-radius: var(--compass-radius, 12px);
    padding: 1.25rem;
}

/* Header */
.analytics-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 1rem;
    flex-wrap: wrap;
    gap: 0.5rem;
}

.analytics-title {
    font-size: 1rem;
    font-weight: 600;
    color: var(--compass-text-primary, #e6edf3);
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
}

.title-icon {
    font-size: 1.1rem;
}

.total-holders {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.85rem;
    color: var(--compass-text-secondary, #7d8590);
}

/* Concentration Grid */
.concentration-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 0.75rem;
    margin-bottom: 1rem;
}

.concentration-card {
    background: rgba(139, 92, 246, 0.06);
    border: 1px solid rgba(139, 92, 246, 0.12);
    border-radius: 8px;
    padding: 0.875rem;
}

.conc-label {
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.35rem;
}

.conc-value {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 1.35rem;
    font-weight: 700;
    color: var(--compass-text-primary, #e6edf3);
}

.conc-bar {
    height: 4px;
    background: rgba(139, 92, 246, 0.15);
    border-radius: 2px;
    margin-top: 0.5rem;
    overflow: hidden;
}

.conc-bar-fill {
    height: 100%;
    background: linear-gradient(90deg, var(--compass-purple), var(--compass-pink));
    border-radius: 2px;
    transition: width 0.5s ease;
}

/* Holders Table Section */
.holders-table-section {
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    padding-top: 1rem;
}

.table-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-bottom: 0.75rem;
}

.table-title {
    font-size: 0.8rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--compass-text-secondary, #7d8590);
}

.table-controls {
    display: flex;
    align-items: center;
    gap: 0.75rem;
}

.filter-toggle {
    display: flex;
    align-items: center;
    gap: 0.35rem;
    cursor: pointer;
    font-size: 0.7rem;
    color: var(--compass-text-secondary, #7d8590);
    user-select: none;
}

.filter-toggle input {
    accent-color: var(--compass-purple, #8b5cf6);
    cursor: pointer;
}

.filter-toggle-label {
    white-space: nowrap;
}

.per-page-selector select {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    color: var(--compass-text-primary, #e6edf3);
    font-size: 0.75rem;
    padding: 0.25rem 0.5rem;
    border-radius: 4px;
}

/* Table styling */
.holders-table {
    font-size: 0.8rem;
}

.holders-table th {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.03em;
    color: var(--compass-text-secondary, #7d8590);
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.15));
    padding: 0.5rem 0.4rem;
}

.holders-table td {
    border-bottom: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
    padding: 0.5rem 0.4rem;
    vertical-align: middle;
}

.col-rank {
    width: 40px;
    color: var(--compass-text-secondary, #7d8590);
}

.holder-info {
    display: flex;
    flex-direction: column;
    gap: 2px;
}

.holder-address {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.75rem;
    color: var(--compass-purple, #8b5cf6);
    text-decoration: none;
}

.holder-address:hover {
    text-decoration: underline;
}

.holder-pubkey {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
    font-size: 0.65rem;
    color: var(--compass-text-secondary, #7d8590);
    opacity: 0.7;
}

.holder-labels {
    display: flex;
    gap: 4px;
    margin-top: 2px;
    flex-wrap: wrap;
}

.label-badge {
    font-size: 0.55rem;
    padding: 1px 5px;
    border-radius: 3px;
    text-transform: uppercase;
    font-weight: 600;
    letter-spacing: 0.02em;
}

/* Label color variants */
.label-program { background: rgba(107, 114, 128, 0.2); color: #9ca3af; }
.label-cex { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-kol { background: rgba(139, 92, 246, 0.2); color: #8b5cf6; }
.label-treasury { background: rgba(16, 185, 129, 0.2); color: #10b981; }
.label-market-maker { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-mm { background: rgba(59, 130, 246, 0.2); color: #3b82f6; }
.label-liquidity-pool { background: rgba(6, 182, 212, 0.2); color: #06b6d4; }
.label-lp { background: rgba(6, 182, 212, 0.2); color: #06b6d4; }
.label-defi { background: rgba(168, 85, 247, 0.2); color: #a855f7; }
.label-nft { background: rgba(249, 115, 22, 0.2); color: #f97316; }
.label-exchange { background: rgba(239, 68, 68, 0.2); color: #ef4444; }
.label-whale { background: rgba(234, 179, 8, 0.2); color: #eab308; }

.mono {
    font-family: 'JetBrains Mono', 'SF Mono', monospace;
}

/* Pagination */
.pagination-controls {
    display: flex;
    justify-content: center;
    align-items: center;
    gap: 1rem;
    margin-top: 0.75rem;
    padding-top: 0.75rem;
    border-top: 1px solid var(--compass-border, rgba(139, 92, 246, 0.1));
}

.page-btn {
    background: rgba(139, 92, 246, 0.1);
    border: 1px solid var(--compass-border, rgba(139, 92, 246, 0.2));
    color: var(--compass-text-primary, #e6edf3);
    padding: 0.35rem 0.75rem;
    border-radius: 4px;
    font-size: 0.75rem;
    cursor: pointer;
    transition: all 0.2s ease;
}

.page-btn:hover:not(:disabled) {
    background: rgba(139, 92, 246, 0.2);
}

.page-btn:disabled {
    opacity: 0.5;
    cursor: not-allowed;
}

.page-info {
    font-size: 0.75rem;
    color: var(--compass-text-secondary, #7d8590);
}

/* Light mode support */
body:not(.dark-mode) .holder-analytics {
    background: #ffffff;
}

body:not(.dark-mode) .conc-value {
    color: #1f2937;
}

body:not(.dark-mode) .holders-table {
    color: #1f2937;
}

body:not(.dark-mode) .per-page-selector select {
    background: #f3f4f6;
    color: #1f2937;
}

body:not(.dark-mode) .page-btn {
    background: #f3f4f6;
    color: #1f2937;
}
</style>
